/**
 * User Response Transformer
 *
 * Transforms backend user response to frontend User interface
 * Removes Date objects and other non-serializable data before Zustand persistence
 */

import type { User } from "@/features/auth/store/authStore"

// Backend user response type (before transformation)
export type BackendUser = {
  id: string
  email: string
  name?: string
  username?: string
  city?: string
  bio?: string
  timezone?: string
  useDeviceTimezone?: boolean
  phoneNumber?: string
  countryCode?: string
  emailVerified: boolean
  profileCreated: boolean
  onboardingCompleted: boolean
  loginType?: string
  hasSetPassword?: boolean
  streetAddress?: string
  street_Address?: string
  street_address?: string
  state?: string
  country?: string
  zip_code?: string
  description?: string
  website_url?: string
  twitter_url?: string
  instagram_url?: string
  facebook_url?: string
  // Add camelCase variants from backend
  websiteUrl?: string
  twitterUrl?: string
  instagramUrl?: string
  facebookUrl?: string
  profileVisibility?: "public" | "private"
  profilePictureUrl?: string | null
  latitude?: number | null
  longitude?: number | null
  // Privacy/Visibility settings - snake_case from backend
  practice_rituals_visibility_access?: string
  teaching_rituals_visibility_access?: string
  attended_events_visibility_access?: string
  stats_visibility_access?: string
  coach_visibility_access?: string
  // Privacy/Visibility settings - camelCase from backend
  practiceRitualsVisibilityAccess?: string
  teachingRitualsVisibilityAccess?: string
  attendedEventsVisibilityAccess?: string
  statsVisibilityAccess?: string
  coachVisibilityAccess?: string
  availableForCoaching?: boolean
  tags?: Array<{ id: string; name: string }>
  profile_photos?: Array<{
    id: string
    model: string
    modelId: string
    fileName: string
    fileType: string
    filePath: string
    fileExtStorageId?: string | null
    isCover: boolean
    is_cover_image?: boolean
    createdBy: string
    updatedBy: string
    createdAt: string | Date
    updatedAt: string | Date
  }>
  uploads?: Array<{
    id: string
    model: string
    modelId: string
    fileName: string
    fileType: string
    filePath: string
    fileExtStorageId?: string | null
    isCover: boolean
    createdBy: string
    updatedBy: string
    createdAt: string | Date
    updatedAt: string | Date
  }>
  role?: {
    id: string
    name: string
    description?: string
  }
  userSettings?: {
    id: string
    userId: string
    calendarEnabled: boolean
    notificationsEnabled: boolean
    contactsEnabled: boolean
    locationEnabled: boolean
    showPracticedRituals: boolean
    showAttendedEvents: boolean
    showCoachInfo: boolean
    showStatsAndStreaks: boolean
    allowProfileView: boolean
  }
  // Follow status fields (only included when viewing another user's profile)
  isFollowing?: boolean
  isPending?: boolean
  // Profile sections
  spiritual_traditions?: Array<{ id: string; title: string; is_custom?: boolean }>
  ethnic_backgrounds?: Array<{ id: string; title: string; is_custom?: boolean }>
  intentions?: Array<{ id: string; title: string; description: string }>
  [key: string]: unknown // Allow additional fields
}

/**
 * Transform backend user response to frontend User schema
 * Filters out Date objects and ensures JSON-serializable data only
 *
 * @param backendUser - User object from backend API response
 * @returns Transformed User object compatible with Zustand persist
 */
export function transformUserResponse(backendUser: BackendUser): User {
  if (!backendUser) {
    console.error("transformUserResponse: backendUser is null or undefined")
    throw new Error("transformUserResponse: backendUser is null or undefined")
  }

  // Helper function to safely convert date to ISO string
  const toISOStringSafe = (date: string | Date | null | undefined): string | undefined => {
    if (!date) return undefined
    if (typeof date === "string") return date
    try {
      const dateObj = new Date(date)
      // Check if date is valid
      if (Number.isNaN(dateObj.getTime())) {
        console.warn("Invalid date value, returning undefined:", date)
        return undefined
      }
      return dateObj.toISOString()
    } catch (error) {
      console.warn("Error converting date to ISO string:", date, error)
      return undefined
    }
  }

  try {
    // Handle both uploads and profile_photos from backend
    const uploads = backendUser.profile_photos || backendUser.uploads || []

    // Transform uploads to frontend format
    const profile_photos = uploads.map(upload => {
      try {
        return {
          id: upload.id,
          model: upload.model,
          model_id: upload.modelId,
          file_name: upload.fileName,
          file_type: upload.fileType,
          filePath: upload.filePath,
          file_ext_storage_id: upload.fileExtStorageId || null,
          isCover: upload.isCover,
          is_cover_image: upload.isCover || false,
          created_by: upload.createdBy,
          updated_by: upload.updatedBy,
          created_at: toISOStringSafe(upload.createdAt) || new Date().toISOString(),
          updated_at: toISOStringSafe(upload.updatedAt) || new Date().toISOString(),
        }
      } catch (error) {
        console.error("Error transforming photo:", upload, error)
        throw error
      }
    })

    const transformed = {
      id: backendUser.id,
      email: backendUser.email,
      name: backendUser.name || undefined,
      username: backendUser.username || undefined,
      city: backendUser.city || undefined,
      bio: backendUser.bio || undefined,
      timezone: backendUser.timezone || undefined,
      useDeviceTimezone: backendUser.useDeviceTimezone,
      phoneNumber: backendUser.phoneNumber || undefined,
      countryCode: backendUser.countryCode || undefined,
      emailVerified: Boolean(backendUser.emailVerified),
      profileCreated: Boolean(backendUser.profileCreated),
      onboardingCompleted: Boolean(backendUser.onboardingCompleted),
      loginType: backendUser.loginType,
      hasSetPassword: backendUser.hasSetPassword,

      // Complete profile fields - handle both camelCase and snake_case for compatibility
      street_address:
        backendUser.streetAddress ||
        backendUser.street_Address ||
        backendUser.street_address ||
        undefined,
      state: backendUser.state || undefined,
      country: backendUser.country || undefined,
      zip_code: backendUser.zip_code || undefined,
      description: backendUser.bio || backendUser.description || undefined,
      // Social links - handle BOTH camelCase (from backend) and snake_case
      website_url: backendUser.websiteUrl || backendUser.website_url || undefined,
      twitter_url: backendUser.twitterUrl || backendUser.twitter_url || undefined,
      instagram_url: backendUser.instagramUrl || backendUser.instagram_url || undefined,
      facebook_url: backendUser.facebookUrl || backendUser.facebook_url || undefined,

      // Additional profile fields
      profileVisibility: backendUser.profileVisibility,
      profilePictureUrl: backendUser.profilePictureUrl || undefined,
      latitude: backendUser.latitude,
      longitude: backendUser.longitude,

      // Privacy/Visibility settings
      // The backend returns both original settings (e.g., "friends") AND computed access (e.g., false)
      // For own profile (stored in auth): use original string settings
      // For other profiles: use computed boolean access
      // Check if original setting exists (string "everyone" or "friends"), otherwise use computed boolean
      practice_rituals_visibility_access:
        typeof backendUser.practiceRitualsVisibilityAccess === "string" &&
        backendUser.practiceRitualsVisibilityAccess
          ? backendUser.practiceRitualsVisibilityAccess
          : ((backendUser as unknown as { practice_rituals_visibility_access?: boolean })
              .practice_rituals_visibility_access ?? true),
      teaching_rituals_visibility_access:
        typeof backendUser.teachingRitualsVisibilityAccess === "string" &&
        backendUser.teachingRitualsVisibilityAccess
          ? backendUser.teachingRitualsVisibilityAccess
          : ((backendUser as unknown as { teaching_rituals_visibility_access?: boolean })
              .teaching_rituals_visibility_access ?? true),
      attended_events_visibility_access:
        typeof backendUser.attendedEventsVisibilityAccess === "string" &&
        backendUser.attendedEventsVisibilityAccess
          ? backendUser.attendedEventsVisibilityAccess
          : ((backendUser as unknown as { attended_events_visibility_access?: boolean })
              .attended_events_visibility_access ?? true),
      stats_visibility_access:
        typeof backendUser.statsVisibilityAccess === "string" && backendUser.statsVisibilityAccess
          ? backendUser.statsVisibilityAccess
          : ((backendUser as unknown as { stats_visibility_access?: boolean })
              .stats_visibility_access ?? true),
      coach_visibility_access:
        typeof backendUser.coachVisibilityAccess === "string" && backendUser.coachVisibilityAccess
          ? backendUser.coachVisibilityAccess
          : ((backendUser as unknown as { coach_visibility_access?: boolean })
              .coach_visibility_access ?? true),

      // Additional profile fields
      availableForCoaching: backendUser.availableForCoaching,

      // Relations
      tags: backendUser.tags || [],
      profile_photos,
      role: backendUser.role,
      userSettings: backendUser.userSettings,

      // Follow status fields (only included when viewing another user's profile)
      isFollowing: backendUser.isFollowing,
      isPending: backendUser.isPending,

      // Profile sections
      spiritual_traditions: backendUser.spiritual_traditions || [],
      ethnic_backgrounds: backendUser.ethnic_backgrounds || [],
      intentions: backendUser.intentions || [],
    }

    return transformed
  } catch (error) {
    console.error("Error in transformUserResponse:", error)
    throw error
  }
}
